import React, { useState, useRef, useEffect } from 'react';
import {
  Copy,
  Trash2,
  Settings,
  Edit3,
  ChevronRight,
  Sparkles,
  Play,
  GripVertical,
  Wand2,
  Image,
  Plus,
  Brain,
  Send
} from 'lucide-react';
import { Chart } from '../lib';
import Markdown from 'markdown-to-jsx';
import styles from './ReportBlock.module.css';
import { realApiData } from '../../views/client/work/reports/reportData';
import { sanitizeRichText } from '../../utils/sanitize';

const ReportBlock = ({ 
  block, 
  mode = 'view',
  onUpdate,
  onDelete,
  onDuplicate,
  editingBlock,
  aiLoadingBlocks,
  aiStreamingBlocks,
  aiGeneratedBlocks,
  setEditingBlock,
  updateBlockContent,
  reportBlockExpandedAccounts,
  setReportBlockExpandedAccounts,
  editingMarkdown,
  setEditingMarkdown,
  aiOriginalContent,
  handleAIGeneration,
  handleAISettingsClick,
  handleEnhanceWithAI,
  index,
  // Drag props
  onDragStart,
  draggable = true,
  // Text selection props
  onTextSelect,
  // Ref management for focus
  textRefs,
  // Variable rendering
  renderTextWithVariables,
  // Text input handler for variable detection
  handleTextInput,
  // Show variable values toggle
  showVariableValues
}) => {
  const { type, content, isPlaceholder } = block;
  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';
  const isEditing = editingBlock === block.id;

  // Refs for contentEditable elements
  const contentEditableRef = useRef(null);
  const lastContentRef = useRef(content.text || '');
  
  
  // State for hero settings modal
  const [showHeroSettings, setShowHeroSettings] = useState(false);
  
  // State for image settings modal
  const [showImageSettings, setShowImageSettings] = useState(false);
  
  // State for report settings modal
  const [showReportSettings, setShowReportSettings] = useState(false);
  
  
  // Update lastContentRef when content changes externally
  useEffect(() => {
    if (content.text !== lastContentRef.current) {
      lastContentRef.current = content.text || '';
    }
  }, [content.text]);

  // Auto-resize textarea when content changes or switching to edit mode, and force update when toggle changes
  useEffect(() => {
    if (isEditMode && textRefs?.current && textRefs.current[block.id]) {
      const element = textRefs.current[block.id];
      if (element) {
        setTimeout(() => {
          if (element.tagName === 'TEXTAREA') {
            element.style.height = 'auto';
            element.style.height = element.scrollHeight + 'px';
          } else if (element.contentEditable === 'true') {
            // Ensure contentEditable elements don't have scroll issues
            element.style.minHeight = '40px';
            element.style.overflow = 'visible';
            
            // Force content update when showVariableValues changes
            if (element.dataset.lastShowVariableValues !== String(showVariableValues)) {
              const currentPlainText = content.text || '';
              let displayContent = currentPlainText;
              
              if (renderTextWithVariables) {
                displayContent = renderTextWithVariables(currentPlainText, 'edit');
              }
              
              const htmlContent = displayContent.replace(/\n/g, '<br>');
              element.innerHTML = sanitizeRichText(htmlContent);
              element.dataset.lastShowVariableValues = String(showVariableValues);
            }
          }
        }, 0);
      }
    }
  }, [isEditMode, content.text, block.id, textRefs, showVariableValues, renderTextWithVariables]);



  // Shared utility functions
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
      return '$0.00';
    }
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '$0.00' : `$${numValue.toLocaleString()}`;
  };

  const formatPercentChange = (current, previous) => {
    if (!previous || previous === 0) {
      return current > 0 ? '+∞%' : '0%';
    }
    const percentChange = ((current - previous) / Math.abs(previous)) * 100;
    const sign = percentChange > 0 ? '+' : '';
    return `${sign}${percentChange.toFixed(1)}%`;
  };

  const getPercentChangeColorClass = (current, previous) => {
    if (!previous || previous === 0) return 'neutral';
    const change = current - previous;
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  };

  // Utility function to apply variable substitution to any text
  const applyVariableSubstitution = (text, mode = 'view') => {
    if (!text || !renderTextWithVariables) return text;
    
    // In edit mode, respect the showVariableValues toggle
    if (mode === 'edit' && !showVariableValues) {
      // Show variable names (raw text with {{}} syntax)
      return text;
    }
    
    // Show variable values (substituted)
    return renderTextWithVariables(text, mode);
  };

  // Utility function for input fields - returns plain text without HTML formatting
  const applyVariableSubstitutionPlainText = (text) => {
    if (!text || !renderTextWithVariables) return text;
    
    // In edit mode, respect the showVariableValues toggle
    if (!showVariableValues) {
      // Show variable names (raw text with {{}} syntax)
      return text;
    }
    
    // Get substituted text and strip HTML tags for input fields
    const substitutedText = renderTextWithVariables(text, 'edit');
    // Remove HTML tags to get plain text
    return substitutedText.replace(/<[^>]*>/g, '');
  };

  // Block type renderers
  const renderTextBlock = () => {
    if (isEditMode) {
      if (isPlaceholder) {
        // Placeholder block - special contentEditable with hover placeholder text
        return (
          <div 
            ref={(el) => {
              if (textRefs?.current && el) {
                textRefs.current[block.id] = el;
              }
            }}
            contentEditable
            suppressContentEditableWarning={true}
            className={styles.placeholderTextArea}
            onInput={(e) => {
              const text = e.target.innerText;
              updateBlockContent(block.id, { text });
              if (text.trim() !== '') {
                // Remove placeholder status when content is added
                updateBlockContent(block.id, { text, isPlaceholder: false });
              }
              // Call handleTextInput for variable detection
              if (handleTextInput) {
                handleTextInput(block.id, text);
              }
            }}
            onMouseEnter={(e) => {
              if (e.target.innerText.trim() === '') {
                e.target.dataset.placeholder = "Press '/' for commands or 'Space' for AI assistant";
              }
            }}
            onMouseLeave={(e) => {
              if (e.target.innerText.trim() === '') {
                e.target.dataset.placeholder = '';
              }
            }}
          />
        );
      } else if (aiGeneratedBlocks?.has(block.id)) {
        // AI-generated text block
        return (
          <div className={styles.aiGeneratedText}>
            {aiLoadingBlocks?.has(block.id) ? (
              <div className={styles.aiLoadingText}>
                <span>LedgerView AI is working</span>
                <div className={styles.loadingDots}>
                  <div className={styles.loadingDot}></div>
                  <div className={styles.loadingDot}></div>
                  <div className={styles.loadingDot}></div>
                </div>
              </div>
            ) : (
              <>
                {editingMarkdown?.[block.id] ? (
                  <textarea
                    value={applyVariableSubstitutionPlainText(content.text || '')}
                    onChange={(e) => updateBlockContent(block.id, { text: e.target.value })}
                    className={styles.aiMarkdownEditor}
                    rows={Math.max(4, (content.text || '').split('\n').length)}
                    autoFocus
                    onMouseUp={(e) => {
                      if (onTextSelect) {
                        onTextSelect(block.id, e);
                      }
                    }}
                    onKeyUp={(e) => {
                      if (onTextSelect) {
                        onTextSelect(block.id, e);
                      }
                    }}
                    onBlur={() => {
                      setEditingMarkdown(prev => {
                        const newState = { ...prev };
                        delete newState[block.id];
                        return newState;
                      });
                    }}
                  />
                ) : (
                  <div className={styles.aiMarkdownContent}>
                    <Markdown options={{
                      overrides: {
                        p: {
                          component: 'div',
                          props: {
                            style: { marginBottom: '1em' }
                          }
                        }
                      },
                      forceBlock: true
                    }}>
                      {(content.text || '').replace(/\n/g, '  \n')}
                    </Markdown>
                  </div>
                )}
              </>
            )}
          </div>
        );
      } else {
        // Always use contentEditable for consistent formatting behavior
        return (
            <div
              ref={(el) => {
                contentEditableRef.current = el;
                if (textRefs?.current && el) {
                  textRefs.current[block.id] = el;
                }
                if (el) {
                  // Always set content when element is first created, or when content changes, or when variable display toggle changes
                  const currentPlainText = content.text || '';
                  const shouldUpdateContent = 
                    el.innerHTML === '' || // Element is empty (newly created)  
                    lastContentRef.current !== currentPlainText || // Content actually changed
                    el.dataset.lastShowVariableValues !== String(showVariableValues); // Toggle state changed
                  
                  if (shouldUpdateContent) {
                    // Save cursor position if element has focus
                    const selection = window.getSelection();
                    let cursorPosition = 0;
                    let hadFocus = false;
                    
                    if (selection.rangeCount > 0 && el.contains(selection.anchorNode)) {
                      hadFocus = true;
                      const range = selection.getRangeAt(0);
                      const preCaretRange = range.cloneRange();
                      preCaretRange.selectNodeContents(el);
                      preCaretRange.setEnd(range.endContainer, range.endOffset);
                      cursorPosition = preCaretRange.toString().length;
                    }
                    
                    // Simple display replacement: just replace {{VARIABLE}} with highlighted values for display
                    let displayContent = currentPlainText;
                    
                    // Render variables if function is available  
                    if (renderTextWithVariables) {
                      displayContent = renderTextWithVariables(currentPlainText, 'edit');
                    }
                    
                    // Set the display content - force browser to parse HTML properly
                    const htmlContent = sanitizeRichText(displayContent.replace(/\n/g, '<br>'));
                    if (el.innerHTML !== htmlContent) {
                      el.innerHTML = htmlContent;
                    }
                    lastContentRef.current = currentPlainText; // Store original plain text, not rendered HTML
                    el.dataset.lastShowVariableValues = String(showVariableValues); // Track toggle state for future comparisons
                    
                    // Restore cursor position only if element had focus
                    if (hadFocus && cursorPosition > 0) {
                      setTimeout(() => {
                        const selection = window.getSelection();
                        const range = document.createRange();
                        let currentPos = 0;
                        const walker = document.createTreeWalker(
                          el,
                          NodeFilter.SHOW_TEXT,
                          null,
                          false
                        );
                        
                        let node;
                        while ((node = walker.nextNode())) {
                          const nodeLength = node.textContent.length;
                          if (currentPos + nodeLength >= cursorPosition) {
                            range.setStart(node, cursorPosition - currentPos);
                            range.setEnd(node, cursorPosition - currentPos);
                            break;
                          }
                          currentPos += nodeLength;
                        }
                        
                        if (range.startContainer) {
                          selection.removeAllRanges();
                          selection.addRange(range);
                        }
                      }, 0);
                    }
                  }
                }
              }}
              contentEditable={true}
              suppressContentEditableWarning={true}
              dangerouslySetInnerHTML={undefined}
              onInput={(e) => {
                // Simple: just extract plain text - variables stay as {{VARIABLE}}
                const plainText = e.target.innerText || e.target.textContent || '';
                
                lastContentRef.current = plainText;
                updateBlockContent(block.id, { text: plainText });
                
                // Call handleTextInput for variable detection
                if (handleTextInput) {
                  handleTextInput(block.id, plainText);
                }
              }}
              onMouseUp={(e) => {
                if (onTextSelect) {
                  onTextSelect(block.id, e);
                }
              }}
              onKeyUp={(e) => {
                if (onTextSelect) {
                  onTextSelect(block.id, e);
                }
              }}
              className={styles.textAreaInput}
              style={{
                minHeight: '40px',
                resize: 'none',
                overflow: 'visible',
                height: 'auto',
                display: 'block',
                width: '100%'
              }}
            />
          );
      }
    }
    
    return (
      <div className={styles.viewTextBlock}>
        <Markdown options={{
          overrides: {
            p: {
              component: 'div',
              props: {
                style: { marginBottom: '1em' }
              }
            }
          },
          forceBlock: true
        }}>
          {(() => {
            let textContent = content.text || '';
            // Render variables if function is available
            if (renderTextWithVariables) {
              textContent = renderTextWithVariables(textContent, 'view');
            }
            return textContent.replace(/\n/g, '  \n');
          })()}
        </Markdown>
      </div>
    );
  };

  const renderTitleBlock = () => {
    if (isEditMode) {
      if (isPlaceholder) {
        // Placeholder title block
        return (
          <div 
            ref={(el) => {
              if (textRefs?.current && el) {
                textRefs.current[block.id] = el;
              }
            }}
            contentEditable
            suppressContentEditableWarning={true}
            className={styles.placeholderTitle}
            onInput={(e) => {
              const text = e.target.innerText;
              updateBlockContent(block.id, { text });
              if (text.trim() !== '') {
                // Remove placeholder status when content is added
                updateBlockContent(block.id, { text, isPlaceholder: false });
              }
              // Call handleTextInput for variable detection
              if (handleTextInput) {
                handleTextInput(block.id, text);
              }
            }}
          >
            {content.text || ''}
          </div>
        );
      } else {
        return (
          <input
            ref={(el) => {
              if (textRefs?.current && el) {
                textRefs.current[block.id] = el;
              }
            }}
            type="text"
            value={applyVariableSubstitutionPlainText(content.text || '')}
            onChange={(e) => updateBlockContent(block.id, { text: e.target.value })}
            className={styles.titleInput}
            placeholder="Enter title..."
          />
        );
      }
    }
    
    return (
      <h1
        className={styles.viewTitle}
        dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(content.text || '', 'view')) }}
      />
    );
  };

  const renderHeadingBlock = () => {
    const HeadingTag = `h${Math.min(content.level || 2, 6)}`;
    
    if (isEditMode) {
      return (
        <div className={styles.headingBlock}>
          <input
            type="text"
            value={applyVariableSubstitutionPlainText(content.text || '')}
            onChange={(e) => updateBlockContent(block.id, { text: e.target.value })}
            className={styles.headingInput}
            placeholder="Enter heading..."
            style={{ fontSize: `${2.5 - (content.level || 2) * 0.2}rem` }}
          />
        </div>
      );
    }
    
    return (
      <HeadingTag 
        className={styles.viewHeading}
        dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(content.text || '', 'view')) }}
      />
    );
  };

  const renderKpiBlock = () => {
    if (isEditMode) {
      return (
        <div className={styles.kpiBlock}>
          <div className={styles.kpiHeader}>
            <input
              type="text"
              value={applyVariableSubstitutionPlainText(content.title || '')}
              onChange={(e) => updateBlockContent(block.id, { title: e.target.value })}
              className={styles.kpiTitleInput}
              placeholder="KPI Title"
            />
          </div>
          <div className={styles.kpiMetricsGrid}>
            {(content.metrics || []).map((metric, idx) => (
              <div key={idx} className={styles.kpiMetricCard}>
                <div className={styles.kpiMetricContent}>
                  <input
                    type="text"
                    value={applyVariableSubstitutionPlainText(metric.label || '')}
                    onChange={(e) => {
                      const newMetrics = [...(content.metrics || [])];
                      newMetrics[idx] = { ...metric, label: e.target.value };
                      updateBlockContent(block.id, { metrics: newMetrics });
                    }}
                    className={styles.kpiMetricLabelInput}
                    placeholder="Metric Label"
                  />
                  <input
                    type="text"
                    value={applyVariableSubstitutionPlainText(metric.value || '')}
                    onChange={(e) => {
                      const newMetrics = [...(content.metrics || [])];
                      newMetrics[idx] = { ...metric, value: e.target.value };
                      updateBlockContent(block.id, { metrics: newMetrics });
                    }}
                    className={styles.kpiMetricValueInput}
                    placeholder="Value"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return (
      <div className={styles.viewKpiBlock}>
        <h3 className={styles.viewKpiTitle} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(content.title || 'KPIs', 'view')) }} />
        <div className={styles.viewKpiGrid}>
          {(content.metrics || []).map((metric, idx) => (
            <div key={idx} className={styles.viewKpiCard}>
              <div className={styles.viewKpiValue} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(metric.value || '', 'view')) }} />
              <div className={styles.viewKpiLabel} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(metric.label || '', 'view')) }} />
              {metric.change && (
                <div className={`${styles.viewKpiChange} ${styles[`viewKpiChange${metric.trend || 'neutral'}`]}`} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(metric.change, 'view')) }} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderChartBlock = () => {
    // For combo charts, ensure multiple datasets with different types
    const prepareComboData = (data) => {
      if (!data || !data.datasets) return data;
      
      // Ensure at least 2 datasets for combo chart
      if (data.datasets.length < 2) {
        return {
          ...data,
          datasets: [
            { ...data.datasets[0], type: 'bar' },
            { 
              label: 'Trend', 
              data: data.datasets[0]?.data || [],
              type: 'line',
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)'
            }
          ]
        };
      }
      
      return data;
    };

    const getChartData = () => {
      const baseData = content.data || {};
      
      switch(content.chartType) {
        case 'combo':
          return prepareComboData(baseData);
        default:
          return baseData;
      }
    };

    if (isEditMode) {
      return (
        <div className={styles.chartBlock}>
          <div className={styles.chartHeader}>
            <input
              type="text"
              value={applyVariableSubstitutionPlainText(content.title || '')}
              onChange={(e) => updateBlockContent(block.id, { title: e.target.value })}
              className={styles.chartTitleInput}
              placeholder="Chart Title"
            />
            <select
              value={content.chartType || 'bar'}
              onChange={(e) => updateBlockContent(block.id, { chartType: e.target.value })}
              className={styles.chartTypeSelect}
            >
              <option value="bar">Bar Chart</option>
              <option value="line">Line Chart</option>
              <option value="pie">Pie Chart</option>
              <option value="doughnut">Doughnut Chart</option>
              <option value="combo">Combo Chart (Bar + Line)</option>
            </select>
          </div>
          <div className={styles.chartContainer}>
            <Chart 
              title={content.title}
              type={content.chartType === 'combo' ? 'bar' : content.chartType || 'bar'}
              data={getChartData()}
              options={{
                ...(content.chartType === 'combo' ? {
                  scales: {
                    y: {
                      beginAtZero: true
                    }
                  }
                } : {})
              }}
            />
          </div>
        </div>
      );
    }
    
    return (
      <div className={styles.viewChartBlock}>
        <h3 className={styles.viewChartTitle} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(content.title || 'Chart', 'view')) }} />
        <div className={styles.viewChartContainer}>
          <Chart 
            title={applyVariableSubstitution(content.title || '', 'view')}
            type={content.chartType === 'combo' ? 'bar' : content.chartType || 'bar'}
            data={getChartData()}
            options={{
              ...(content.chartType === 'waterfall' ? {
                scales: {
                  y: {
                    beginAtZero: true
                  }
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y;
                        if (Array.isArray(value)) {
                          return `Change: ${(value[1] - value[0]).toLocaleString()}`;
                        }
                        return value.toLocaleString();
                      }
                    }
                  }
                }
              } : {}),
              ...(content.chartType === 'combo' ? {
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              } : {})
            }}
          />
        </div>
      </div>
    );
  };

  const renderTableBlock = () => {
    const { showTotals = false, conditionalFormatting = false, numberColumns = [] } = content;
    
    if (isEditMode) {
      const headers = content.headers || ['Column 1', 'Column 2'];
      const rows = content.rows || [['', '']];

      const addColumn = () => {
        const newHeaders = [...headers, `Column ${headers.length + 1}`];
        const newRows = rows.map(row => [...row, '']);
        updateBlockContent(block.id, { 
          headers: newHeaders, 
          rows: newRows 
        });
      };

      const removeColumn = (colIndex) => {
        if (headers.length <= 1) return; // Keep at least one column
        const newHeaders = headers.filter((_, idx) => idx !== colIndex);
        const newRows = rows.map(row => row.filter((_, idx) => idx !== colIndex));
        const newNumberColumns = numberColumns.filter(col => col !== colIndex).map(col => col > colIndex ? col - 1 : col);
        updateBlockContent(block.id, { 
          headers: newHeaders, 
          rows: newRows,
          numberColumns: newNumberColumns
        });
      };

      const addRow = () => {
        const newRow = new Array(headers.length).fill('');
        const newRows = [...rows, newRow];
        updateBlockContent(block.id, { rows: newRows });
      };

      const removeRow = (rowIndex) => {
        if (rows.length <= 1) return; // Keep at least one row
        const newRows = rows.filter((_, idx) => idx !== rowIndex);
        updateBlockContent(block.id, { rows: newRows });
      };

      const toggleNumberColumn = (colIndex) => {
        const newNumberColumns = numberColumns.includes(colIndex)
          ? numberColumns.filter(col => col !== colIndex)
          : [...numberColumns, colIndex];
        updateBlockContent(block.id, { numberColumns: newNumberColumns });
      };

      return (
        <div className={styles.tableBlock}>
          <input
            type="text"
            value={applyVariableSubstitutionPlainText(content.title || '')}
            onChange={(e) => updateBlockContent(block.id, { title: e.target.value })}
            className={styles.tableTitleInput}
            placeholder="Table Title"
          />
          
          <div className={styles.tableControls}>
            <button 
              onClick={addRow}
              className={styles.tableControlBtn}
              title="Add Row"
            >
              + Row
            </button>
            <button 
              onClick={addColumn}
              className={styles.tableControlBtn}
              title="Add Column"
            >
              + Column
            </button>
            <label className={styles.tableCheckbox}>
              <input
                type="checkbox"
                checked={showTotals}
                onChange={(e) => updateBlockContent(block.id, { showTotals: e.target.checked })}
              />
              Show Totals
            </label>
            <label className={styles.tableCheckbox}>
              <input
                type="checkbox"
                checked={conditionalFormatting}
                onChange={(e) => updateBlockContent(block.id, { conditionalFormatting: e.target.checked })}
              />
              Conditional Formatting
            </label>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.dataTable}>
              <thead className={styles.tableHeader}>
                <tr>
                  {headers.map((header, idx) => (
                    <th key={idx} className={styles.tableHeaderCell}>
                      <div className={styles.headerCellContent}>
                        <input
                          type="text"
                          value={applyVariableSubstitutionPlainText(header)}
                          onChange={(e) => {
                            const newHeaders = [...headers];
                            newHeaders[idx] = e.target.value;
                            updateBlockContent(block.id, { headers: newHeaders });
                          }}
                          className={styles.tableHeaderInput}
                          placeholder="Header"
                        />
                        <label className={styles.numberColumnToggle} title="Format as number">
                          <input
                            type="checkbox"
                            checked={numberColumns.includes(idx)}
                            onChange={() => toggleNumberColumn(idx)}
                          />
                          #
                        </label>
                        {headers.length > 1 && (
                          <button 
                            onClick={() => removeColumn(idx)}
                            className={styles.removeColumnBtn}
                            title="Remove Column"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      <td key={cellIdx} className={styles.tableCell}>
                        <input
                          type="text"
                          value={applyVariableSubstitutionPlainText(cell)}
                          onChange={(e) => {
                            const newRows = [...rows];
                            newRows[rowIdx] = [...row];
                            newRows[rowIdx][cellIdx] = e.target.value;
                            updateBlockContent(block.id, { rows: newRows });
                          }}
                          className={styles.tableCellInput}
                        />
                      </td>
                    ))}
                    {rows.length > 1 && (
                      <td className={styles.rowControlCell}>
                        <button 
                          onClick={() => removeRow(rowIdx)}
                          className={styles.removeRowBtn}
                          title="Remove Row"
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    
    // View mode with enhanced features
    const parseNumber = (value) => {
      const num = parseFloat(value.toString().replace(/[^0-9.-]/g, ''));
      return isNaN(num) ? 0 : num;
    };

    const formatNumber = (value) => {
      const num = parseNumber(value);
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const calculateColumnTotal = (colIdx) => {
      return content.rows.reduce((sum, row) => {
        const value = parseNumber(row[colIdx] || 0);
        return sum + value;
      }, 0);
    };

    const getCellClass = (value, colIdx) => {
      if (!conditionalFormatting || !numberColumns.includes(colIdx)) return '';
      
      const num = parseNumber(value);
      if (num > 0) return styles.positiveValue;
      if (num < 0) return styles.negativeValue;
      return '';
    };

    return (
      <div className={styles.viewTableBlock}>
        <h3 className={styles.viewTableTitle} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(content.title || 'Table', 'view')) }} />
        <div className={styles.viewTableContainer}>
          <table className={styles.viewTable}>
            <thead className={styles.viewTableHeader}>
              <tr>
                {(content.headers || []).map((header, idx) => (
                  <th key={idx} className={styles.viewTableHeaderCell} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(header || '', 'view')) }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {(content.rows || []).map((row, rowIdx) => (
                <tr key={rowIdx} className={styles.viewTableRow}>
                  {row.map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      className={`${styles.viewTableCell} ${getCellClass(cell, cellIdx)}`}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(numberColumns.includes(cellIdx) ? formatNumber(cell) : applyVariableSubstitution(cell || '', 'view'))
                      }}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
            {showTotals && content.rows && content.rows.length > 0 && (
              <tfoot className={styles.viewTableFooter}>
                <tr>
                  {content.headers.map((_, idx) => (
                    <td key={idx} className={styles.viewTableTotalCell}>
                      {idx === 0 ? 'Total' : 
                       numberColumns.includes(idx) ? formatNumber(calculateColumnTotal(idx)) : ''}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  };

  const renderQuoteBlock = () => {
    if (isEditMode) {
      return (
        <div className={styles.quoteBlock}>
          <textarea
            value={applyVariableSubstitutionPlainText(content.text || '')}
            onChange={(e) => updateBlockContent(block.id, { text: e.target.value })}
            className={styles.quoteTextInput}
            placeholder="Enter quote..."
            rows={3}
          />
          <input
            type="text"
            value={applyVariableSubstitutionPlainText(content.author || '')}
            onChange={(e) => updateBlockContent(block.id, { author: e.target.value })}
            className={styles.quoteAuthorInput}
            placeholder="Author (optional)"
          />
        </div>
      );
    }
    
    return (
      <div className={styles.viewQuoteBlock}>
        <blockquote className={styles.viewQuote}>
          "<span dangerouslySetInnerHTML={{ __html: applyVariableSubstitution(content.text || '', 'view') }} />"
          {content.author && (
            <cite className={styles.viewQuoteAuthor}>— <span dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(content.author, 'view')) }} /></cite>
          )}
        </blockquote>
      </div>
    );
  };

  const renderDividerBlock = () => {
    return <hr className={styles.divider} />;
  };

  const renderVideoBlock = () => {
    if (isEditMode) {
      return (
        <div className={styles.videoBlock}>
          <input
            type="text"
            value={applyVariableSubstitutionPlainText(content.title || '')}
            onChange={(e) => updateBlockContent(block.id, { title: e.target.value })}
            className={styles.videoTitleInput}
            placeholder="Video Title"
          />
          <div className={styles.viewVideoPlaceholder}>
            <Play size={48} />
            <span>Click settings to add video URL</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className={styles.viewVideoBlock}>
        <h3 className={styles.viewVideoTitle} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(content.title || 'Video', 'view')) }} />
        <div className={styles.viewVideoPlaceholder}>
          <Play size={48} />
          <span>Video URL required</span>
        </div>
      </div>
    );
  };

  const renderFinancialSummaryBlock = () => {
    const renderKpiGrid = (kpis) => (
      <div className={styles.viewKpiGrid}>
        {(kpis || []).map((kpi, idx) => (
          <div key={idx} className={styles.viewKpiCard}>
            <div className={styles.viewKpiValue} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(kpi.value || '', 'view')) }} />
            <div className={styles.viewKpiLabel} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(kpi.label || '', 'view')) }} />
            <div className={`${styles.viewKpiChange} ${styles[`viewKpiChange${kpi.trend || 'neutral'}`]}`} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(kpi.change || '', 'view')) }} />
          </div>
        ))}
      </div>
    );

    const renderChart = (chartData, chartType, title) => {
      if (!chartData?.data || chartData.data.length === 0) return null;
      
      return (
        <div className={styles.viewChartBlock}>
          <h3 className={styles.viewChartTitle} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(title || '', 'view')) }} />
          <div className={styles.viewChartContainer}>
            <Chart 
              title={applyVariableSubstitution(title || '', 'view')}
              chartType={chartType}
              data={chartData.data}
            />
          </div>
        </div>
      );
    };

    return (
      <div className={styles.viewFinancialSummary}>
        {renderKpiGrid(content.kpis)}
        {content.lineChart && renderChart(content.lineChart, 'line', content.lineChart.title)}
        {content.pieChart && renderChart(content.pieChart, 'pie', content.pieChart.title)}
      </div>
    );
  };

  const renderReportBlock = () => {
    // Function to get grouped accounts organized by type with subtotals
    const getGroupedAccounts = (reportType, accounts) => {
      // Always use realApiData as the primary source
      accounts = realApiData.accounts;
      
      const filterAccountsByType = (types) => {
        return accounts.filter(account => {
          if (!types.includes(account.type)) return false;
          
          // Filter out accounts with $0 amounts
          const amount = getAccountAmount(account, reportType);
          return Math.abs(amount) > 0;
        });
      };

      const sortAccountsAlphabetically = (accounts) => {
        return accounts.sort((a, b) => a.name.localeCompare(b.name));
      };
      
      switch (reportType) {
        case 'profit-loss':
          return [
            {
              title: 'INCOME',
              accounts: sortAccountsAlphabetically(filterAccountsByType(['Income', 'Other Income'])),
              showSubtotal: true
            },
            {
              title: 'COST OF GOODS SOLD',
              accounts: sortAccountsAlphabetically(filterAccountsByType(['Cost of Goods Sold'])),
              showSubtotal: true
            },
            {
              title: 'EXPENSES', 
              accounts: sortAccountsAlphabetically(filterAccountsByType(['Expense', 'Other Expense'])),
              showSubtotal: true
            }
          ];
          
        case 'balance-sheet':
          return [
            {
              title: 'ASSETS',
              accounts: sortAccountsAlphabetically(filterAccountsByType(['Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset'])),
              showSubtotal: true
            },
            {
              title: 'LIABILITIES',
              accounts: sortAccountsAlphabetically(filterAccountsByType(['Accounts Payable', 'Credit Card', 'Other Current Liability', 'Long Term Liability'])),
              showSubtotal: true
            },
            {
              title: 'EQUITY',
              accounts: sortAccountsAlphabetically(filterAccountsByType(['Equity'])),
              showSubtotal: true
            }
          ];
          
        case 'trial-balance':
          // For trial balance, just return all accounts in one group, sorted by type then alphabetically
          const allAccounts = accounts.filter(account => {
            const amount = getAccountAmount(account, reportType);
            return Math.abs(amount) > 0;
          });
          
          const typeOrder = {
            'Bank': 1, 'Accounts Receivable': 2, 'Other Current Asset': 3, 'Fixed Asset': 4,
            'Accounts Payable': 5, 'Credit Card': 6, 'Other Current Liability': 7, 'Long Term Liability': 8,
            'Equity': 9,
            'Income': 10, 'Other Income': 11,
            'Cost of Goods Sold': 12,
            'Expense': 13, 'Other Expense': 14
          };
          
          const sortedAccounts = allAccounts.sort((a, b) => {
            const orderA = typeOrder[a.type] || 999;
            const orderB = typeOrder[b.type] || 999;
            if (orderA !== orderB) return orderA - orderB;
            return a.name.localeCompare(b.name);
          });
          
          return [{ title: '', accounts: sortedAccounts, showSubtotal: false }];
          
        default:
          return [{ title: '', accounts: [], showSubtotal: false }];
      }
    };

    // Helper function to get account amount based on report type
    const getAccountAmount = (account, reportType) => {
      if (reportType === 'trial-balance' || reportType === 'balance-sheet') {
        return account.val || 0;
      } else {
        // P&L uses p_l values if available, otherwise val
        return account.p_l !== undefined ? account.p_l : account.val || 0;
      }
    };

    // Function to calculate section subtotals and overall totals
    const calculateTotals = (groupedAccounts, reportType) => {
      const sectionTotals = {};
      
      groupedAccounts.forEach(group => {
        if (group.accounts.length > 0) {
          const total = group.accounts.reduce((sum, acc) => sum + Math.abs(getAccountAmount(acc, reportType)), 0);
          sectionTotals[group.title] = total;
        }
      });
      
      if (reportType === 'profit-loss') {
        const income = sectionTotals['INCOME'] || 0;
        const cogs = sectionTotals['COST OF GOODS SOLD'] || 0;
        const expenses = sectionTotals['EXPENSES'] || 0;
        const grossProfit = income - cogs;
        const netIncome = grossProfit - expenses;
        
        return { ...sectionTotals, grossProfit, netIncome };
      } else if (reportType === 'balance-sheet') {
        const liabilities = sectionTotals['LIABILITIES'] || 0;
        const equity = sectionTotals['EQUITY'] || 0;
        const totalLiabilitiesEquity = liabilities + equity;
        
        return { ...sectionTotals, totalLiabilitiesEquity };
      } else if (reportType === 'trial-balance') {
        const allAccounts = groupedAccounts[0]?.accounts || [];
        const totalDebits = allAccounts
          .filter(acc => ['Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset', 'Expense', 'Other Expense', 'Cost of Goods Sold'].includes(acc.type))
          .reduce((sum, acc) => sum + Math.abs(getAccountAmount(acc, reportType)), 0);
        
        const totalCredits = allAccounts
          .filter(acc => ['Accounts Payable', 'Credit Card', 'Other Current Liability', 'Long Term Liability', 'Equity', 'Income', 'Other Income'].includes(acc.type))
          .reduce((sum, acc) => sum + Math.abs(getAccountAmount(acc, reportType)), 0);
        
        return { totalDebits, totalCredits };
      }
      return sectionTotals;
    };
    
    const getReportTitle = (reportType) => {
      switch (reportType) {
        case 'profit-loss':
          return 'Profit & Loss Statement';
        case 'balance-sheet':
          return 'Balance Sheet';
        case 'trial-balance':
          return 'Trial Balance';
        default:
          return 'Financial Report';
      }
    };

    const reportType = content.reportType || 'profit-loss';
    const groupedAccounts = getGroupedAccounts(reportType, content.data?.accounts);
    const totals = calculateTotals(groupedAccounts, reportType);
    
    // Get total account count for preview
    const totalAccountCount = groupedAccounts.reduce((count, group) => count + group.accounts.length, 0);

    const renderAccountRow = (account, level = 0, blockId, isBalanceSheet = false, isTrialBalance = false) => {
      const expandKey = `${blockId}-${account.accountId}`;
      const isExpanded = reportBlockExpandedAccounts?.[expandKey];
      const hasSubAccounts = account.sub && account.sub.length > 0;
      const indentStyle = { paddingLeft: `${level * 20 + 16}px` };

      let displayAmount, displayPreviousAmount;
      
      if (isTrialBalance) {
        // For trial balance, show actual values (not absolute) to maintain sign
        displayAmount = account.val || 0;
        displayPreviousAmount = account.val_previous || account.val || 0;
      } else if (isBalanceSheet) {
        // Balance sheet uses val (balance) field
        displayAmount = Math.abs(account.val || 0);
        displayPreviousAmount = Math.abs(account.val_previous || account.val || 0);
      } else {
        // P&L uses p_l values if available (period activity), otherwise val (balance)
        // For P&L accounts, p_l represents the period activity
        displayAmount = Math.abs(account.p_l !== undefined ? account.p_l : account.val || 0);
        displayPreviousAmount = Math.abs(account.p_l_previous !== undefined ? account.p_l_previous : 
                                       (account.p_l !== undefined ? account.p_l : account.val) || 0);
      }

      const percentChangeClass = getPercentChangeColorClass(displayAmount, displayPreviousAmount);

      return (
        <React.Fragment key={account.accountId}>
          <tr className={isViewMode ? styles.viewReportAccountRow : styles.reportAccountRow}>
            <td className={isViewMode ? styles.viewReportAccountCell : styles.reportAccountCell}>
              <div 
                className={isViewMode ? styles.viewReportAccountCellInner : styles.reportAccountCellInner}
                style={indentStyle}
              >
                {hasSubAccounts ? (
                  <span 
                    className={isViewMode ? styles.viewReportExpandToggle : styles.reportExpandToggle}
                    onClick={() => {
                      if (isEditMode && setReportBlockExpandedAccounts) {
                        setReportBlockExpandedAccounts(prev => ({
                          ...prev,
                          [expandKey]: !prev[expandKey]
                        }));
                      }
                    }}
                    style={{ cursor: isEditMode ? 'pointer' : 'default' }}
                  >
                    {isExpanded ? (
                      <ChevronRight className={isViewMode ? styles.viewReportChevronIcon : styles.reportChevronIcon} size={16} style={{ transform: 'rotate(90deg)' }} />
                    ) : (
                      <ChevronRight className={isViewMode ? styles.viewReportChevronIcon : styles.reportChevronIcon} size={16} />
                    )}
                  </span>
                ) : (
                  <div className={isViewMode ? styles.viewReportExpandPlaceholder : styles.reportExpandPlaceholder} />
                )}
                <span 
                  className={isViewMode ? styles.viewReportAccountName : styles.reportAccountName} 
                  style={{ paddingLeft: level > 0 ? '8px' : '0' }}
                >
                  {account.name}
                </span>
              </div>
            </td>
            {isTrialBalance ? (
              <>
                <td className={isViewMode ? styles.viewReportAccountAmount : styles.reportAccountAmount}>
                  {(displayAmount > 0 && (account.isDebit || ['Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset', 'Expense', 'Other Expense', 'Cost of Goods Sold'].includes(account.type))) 
                   ? formatCurrency(Math.abs(displayAmount)) : '-'}
                </td>
                <td className={isViewMode ? styles.viewReportAccountAmount : styles.reportAccountAmount}>
                  {(displayAmount > 0 && (!account.isDebit || ['Accounts Payable', 'Credit Card', 'Other Current Liability', 'Long Term Liability', 'Equity', 'Income', 'Other Income'].includes(account.type))) 
                   ? formatCurrency(Math.abs(displayAmount)) : '-'}
                </td>
              </>
            ) : (
              <>
                <td className={isViewMode ? styles.viewReportAccountAmount : styles.reportAccountAmount}>
                  {formatCurrency(displayAmount)}
                </td>
                <td className={isViewMode ? styles.viewReportAccountAmount : styles.reportAccountAmount}>
                  {formatCurrency(displayPreviousAmount)}
                </td>
                <td className={`${isViewMode ? styles.viewReportPercentChange : styles.reportPercentChange} ${styles[percentChangeClass]}`}>
                  {formatPercentChange(displayAmount, displayPreviousAmount)}
                </td>
              </>
            )}
          </tr>
          {isExpanded && hasSubAccounts && account.sub.map(subAccount => 
            renderAccountRow(subAccount, level + 1, blockId, isBalanceSheet, isTrialBalance)
          )}
        </React.Fragment>
      );
    };

    // Settings Modal for Report Block
    const renderReportSettingsModal = () => {
      if (!showReportSettings) return null;
      
      return (
        <div className={styles.settingsModal}>
          <div className={styles.settingsModalContent}>
            <div className={styles.settingsModalHeader}>
              <h3>Report Settings</h3>
              <button 
                onClick={() => setShowReportSettings(false)}
                className={styles.settingsModalClose}
              >
                ×
              </button>
            </div>
            
            <div className={styles.settingsModalBody}>
              <div className={styles.settingsGroup}>
                <label className={styles.settingsLabel}>Report Type</label>
                <select
                  value={reportType}
                  onChange={(e) => updateBlockContent(block.id, { reportType: e.target.value })}
                  className={styles.settingsSelect}
                >
                  <option value="profit-loss">Profit & Loss Statement</option>
                  <option value="balance-sheet">Balance Sheet</option>
                  <option value="trial-balance">Trial Balance</option>
                </select>
              </div>

              <div className={styles.settingsGroup}>
                <div className={styles.settingsPreview}>
                  <strong>Preview:</strong>
                  <div>{getReportTitle(reportType)} with {totalAccountCount} accounts</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <>
        <div className={isViewMode ? styles.viewReportBlock : styles.reportBlock}>
          <div className={isViewMode ? styles.viewReportTitle : styles.reportTitle}>
            {applyVariableSubstitution(content.title || getReportTitle(reportType), 'view')}
          </div>
          {content.dateRange && (
            <div className={isViewMode ? styles.viewReportDateRange : styles.reportDateRange}>
              {applyVariableSubstitution(content.dateRange, 'view')}
            </div>
          )}
          <div className={isViewMode ? styles.viewReportTableContainer : styles.reportTableContainer}>
            <table className={isViewMode ? styles.viewReportTable : styles.reportTable}>
              <thead className={isViewMode ? styles.viewReportTableHeader : styles.reportTableHeader}>
                <tr>
                  <th className={isViewMode ? styles.viewReportTableHeaderCell : styles.reportTableHeaderCell}>Account</th>
                  {reportType === 'trial-balance' ? (
                    <>
                      <th className={isViewMode ? styles.viewReportTableHeaderCell : styles.reportTableHeaderCell}>Debit</th>
                      <th className={isViewMode ? styles.viewReportTableHeaderCell : styles.reportTableHeaderCell}>Credit</th>
                    </>
                  ) : (
                    <>
                      <th className={isViewMode ? styles.viewReportTableHeaderCell : styles.reportTableHeaderCell}>Current Period</th>
                      <th className={isViewMode ? styles.viewReportTableHeaderCell : styles.reportTableHeaderCell}>Previous Period</th>
                      <th className={isViewMode ? styles.viewReportTableHeaderCell : styles.reportTableHeaderCell}>% Change</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className={isViewMode ? styles.viewReportTableBody : styles.reportTableBody}>
                {groupedAccounts.map((group, groupIndex) => {
                  const rows = [];
                  
                  // Add section header if there's a title
                  if (group.title) {
                    rows.push(
                      <tr key={`header-${groupIndex}`} className={isViewMode ? styles.viewReportSectionHeader : styles.reportSectionHeader}>
                        <td colSpan={reportType === 'trial-balance' ? 3 : 4}>
                          <strong>{group.title}</strong>
                        </td>
                      </tr>
                    );
                  }
                  
                  // Add accounts in this section
                  group.accounts.forEach(account => {
                    rows.push(
                      renderAccountRow(
                        account, 
                        0, 
                        block.id, 
                        reportType === 'balance-sheet',
                        reportType === 'trial-balance'
                      )
                    );
                  });
                  
                  // Add subtotal if this section should show one
                  if (group.showSubtotal && group.accounts.length > 0) {
                    const sectionTotal = totals[group.title] || 0;
                    rows.push(
                      <tr key={`subtotal-${groupIndex}`} className={isViewMode ? styles.viewReportSubtotalRow : styles.reportSubtotalRow}>
                        <td><strong>Total {group.title}</strong></td>
                        {reportType === 'trial-balance' ? (
                          <>
                            <td><strong>{group.title === 'ASSETS' || group.title === 'EXPENSES' || group.title === 'COST OF GOODS SOLD' ? formatCurrency(sectionTotal) : ''}</strong></td>
                            <td><strong>{group.title === 'LIABILITIES' || group.title === 'EQUITY' || group.title === 'INCOME' ? formatCurrency(sectionTotal) : ''}</strong></td>
                          </>
                        ) : (
                          <>
                            <td><strong>{formatCurrency(sectionTotal)}</strong></td>
                            <td></td>
                            <td></td>
                          </>
                        )}
                      </tr>
                    );
                  }
                  
                  return rows;
                })}
              </tbody>
              {/* Totals Footer */}
              <tfoot className={isViewMode ? styles.viewReportTableFooter : styles.reportTableFooter}>
                {reportType === 'profit-loss' && (
                  <>
                    {(totals['COST OF GOODS SOLD'] || 0) > 0 && (
                      <tr className={isViewMode ? styles.viewReportSubtotalRow : styles.reportSubtotalRow}>
                        <td><strong>Gross Profit</strong></td>
                        <td><strong>{formatCurrency(totals.grossProfit)}</strong></td>
                        <td></td>
                        <td></td>
                      </tr>
                    )}
                    <tr className={isViewMode ? styles.viewReportTotalRow : styles.reportTotalRow}>
                      <td><strong>Net Income</strong></td>
                      <td><strong>{formatCurrency(totals.netIncome)}</strong></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </>
                )}
                {reportType === 'balance-sheet' && (
                  <tr className={isViewMode ? styles.viewReportTotalRow : styles.reportTotalRow}>
                    <td><strong>Total Liabilities + Equity</strong></td>
                    <td><strong>{formatCurrency(totals.totalLiabilitiesEquity)}</strong></td>
                    <td></td>
                    <td></td>
                  </tr>
                )}
                {reportType === 'trial-balance' && (
                  <tr className={isViewMode ? styles.viewReportTotalRow : styles.reportTotalRow}>
                    <td><strong>Totals</strong></td>
                    <td><strong>{formatCurrency(totals.totalDebits)}</strong></td>
                    <td><strong>{formatCurrency(totals.totalCredits)}</strong></td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </div>
        {renderReportSettingsModal()}
      </>
    );
  };


  const renderImageBlock = () => {
    if (isEditMode) {
      return (
        <div className={styles.imageBlock}>
          {content.url ? (
            <img 
              src={content.url} 
              alt={content.alt ? applyVariableSubstitution(content.alt, 'view').replace(/<[^>]*>/g, '') : 'Image'} 
              className={styles.imagePreview}
            />
          ) : (
            <div className={styles.imagePlaceholder}>
              <Image size={48} />
              <span>Click settings to add image</span>
            </div>
          )}

          {/* Image Settings Modal */}
          {showImageSettings && (
            <div className={styles.settingsModal}>
              <div className={styles.settingsModalContent}>
                <div className={styles.settingsModalHeader}>
                  <h3>Image Settings</h3>
                  <button 
                    onClick={() => setShowImageSettings(false)}
                    className={styles.settingsModalClose}
                  >
                    ×
                  </button>
                </div>
                
                <div className={styles.settingsModalBody}>
                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Image URL</label>
                    <input
                      type="text"
                      value={applyVariableSubstitutionPlainText(content.url || '')}
                      onChange={(e) => updateBlockContent(block.id, { url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className={styles.settingsInput}
                    />
                    {content.url && (
                      <button
                        onClick={() => updateBlockContent(block.id, { url: '', alt: '' })}
                        className={styles.clearImageBtn}
                      >
                        Clear Image
                      </button>
                    )}
                  </div>

                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Upload Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Validate file type
                          const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
                          if (!validImageTypes.includes(file.type)) {
                            alert('Invalid file type. Please upload a valid image (JPEG, PNG, GIF, WebP, or SVG).');
                            e.target.value = '';
                            return;
                          }

                          // Validate file size (max 5MB)
                          const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
                          if (file.size > maxSizeInBytes) {
                            alert('File size exceeds 5MB. Please upload a smaller image.');
                            e.target.value = '';
                            return;
                          }

                          const reader = new FileReader();
                          reader.onload = (e) => {
                            updateBlockContent(block.id, {
                              url: e.target.result,
                              alt: file.name
                            });
                          };
                          reader.onerror = () => {
                            alert('Error reading file. Please try again.');
                            e.target.value = '';
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className={styles.fileInput}
                    />
                  </div>

                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Alt Text (for accessibility)</label>
                    <input
                      type="text"
                      value={applyVariableSubstitutionPlainText(content.alt || '')}
                      onChange={(e) => updateBlockContent(block.id, { alt: e.target.value })}
                      placeholder="Description of the image"
                      className={styles.settingsInput}
                    />
                  </div>

                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Image Width</label>
                    <select
                      value={content.width || 'auto'}
                      onChange={(e) => updateBlockContent(block.id, { width: e.target.value })}
                      className={styles.settingsSelect}
                    >
                      <option value="auto">Auto</option>
                      <option value="25%">25%</option>
                      <option value="50%">50%</option>
                      <option value="75%">75%</option>
                      <option value="100%">100%</option>
                    </select>
                  </div>

                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Alignment</label>
                    <select
                      value={content.alignment || 'center'}
                      onChange={(e) => updateBlockContent(block.id, { alignment: e.target.value })}
                      className={styles.settingsSelect}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return content.url ? (
      <img 
        src={content.url} 
        alt={content.alt ? applyVariableSubstitution(content.alt, 'view').replace(/<[^>]*>/g, '') : 'Image'} 
        className={styles.viewImage}
        style={{
          width: content.width || 'auto',
          textAlign: content.alignment || 'center',
          display: 'block',
          margin: content.alignment === 'center' ? '0 auto' : 
                 content.alignment === 'right' ? '0 0 0 auto' : '0'
        }}
      />
    ) : null;
  };




  // Advanced block implementations added below

  // AI Block renderer
  const renderAIBlock = () => {
    const hasContent = content.text && content.text.trim().length > 0;
    const isLoading = aiLoadingBlocks?.has(block.id);
    const isStreaming = aiStreamingBlocks?.has(block.id);
    
    if (isEditMode && !hasContent) {
      // Show prompt interface (normal or working state)
      return (
        <div className={styles.aiPromptInterface}>
          <div className={styles.aiPromptHeader}>
            <Brain size={16} className={styles.aiIcon} />
            <span>AI Content Generation</span>
            {isLoading && (
              <div className={styles.workingIndicator}>
                <div className={styles.loadingDot}></div>
                <div className={styles.loadingDot}></div>
                <div className={styles.loadingDot}></div>
              </div>
            )}
          </div>
          
          <div className={styles.aiPromptField}>
            <label className={styles.aiPromptLabel}>Prompt:</label>
            <textarea
              value={content.prompt || ''}
              onChange={(e) => !isLoading && updateBlockContent(block.id, { prompt: e.target.value })}
              className={`${styles.aiPromptTextarea} ${isLoading ? styles.aiPromptTextareaWorking : ''}`}
              placeholder="Describe what you want the AI to generate..."
              rows={3}
              readOnly={isLoading}
            />
          </div>
          
          <div className={styles.aiPromptActions}>
            <button
              onClick={() => handleAIGeneration?.(block.id, content.prompt)}
              className={styles.aiGenerateButton}
              disabled={!content.prompt || content.prompt.trim().length === 0 || isLoading}
            >
              <Send size={14} />
              {isLoading ? 'Generating...' : 'Generate Content'}
            </button>
          </div>
          
          {isLoading && (
            <div className={styles.aiWorkingStatus}>
              <span>LedgerView AI is working...</span>
            </div>
          )}
        </div>
      );
    }
    
    if (hasContent) {
      // Show generated content with editable prompt
      if (isEditMode) {
        if (isStreaming) {
          // During streaming, show content like a regular text block being typed with proper markdown
          return (
            <div className={styles.streamingTextBlock}>
              <div className={styles.aiMarkdownContent}>
                <Markdown options={{
                  overrides: {
                    p: {
                      component: 'div',
                      props: {
                        style: { marginBottom: '1em' }
                      }
                    }
                  },
                  forceBlock: true
                }}>
                  {(content.text || '').replace(/\n/g, '  \n')}
                </Markdown>
              </div>
              <div className={styles.streamingCursor}></div>
            </div>
          );
        } else {
          // After streaming is complete, show either clean text block or prompt editor
          if (editingMarkdown?.[block.id]?.isEditing || editingMarkdown?.[block.id] === true) {
            // Show full AI prompt interface like when creating from scratch
            return (
              <div className={styles.aiPromptInterface}>
                <div className={styles.aiPromptHeader}>
                  <Brain size={16} className={styles.aiIcon} />
                  <span>AI Content Generation</span>
                </div>
                
                <div className={styles.aiPromptField}>
                  <label className={styles.aiPromptLabel}>Prompt:</label>
                  <textarea
                    value={content.prompt || ''}
                    onChange={(e) => updateBlockContent(block.id, { prompt: e.target.value })}
                    className={styles.aiPromptTextarea}
                    placeholder="Describe what you want the AI to generate..."
                    rows={3}
                    autoFocus
                  />
                </div>
                
                <div className={styles.aiPromptActions}>
                  <button
                    onClick={() => handleAIGeneration?.(block.id, content.prompt)}
                    className={styles.aiGenerateButton}
                    disabled={!content.prompt || content.prompt.trim().length === 0}
                  >
                    <Send size={14} />
                    Generate Content
                  </button>
                  <button
                    onClick={() => {
                      // Restore original prompt on cancel
                      const originalPrompt = editingMarkdown?.[block.id]?.originalPrompt;
                      if (originalPrompt !== undefined) {
                        updateBlockContent(block.id, { prompt: originalPrompt });
                      }
                      // Close editor
                      setEditingMarkdown(prev => {
                        const newState = { ...prev };
                        delete newState[block.id];
                        return newState;
                      });
                    }}
                    className={styles.aiCancelButton}
                  >
                    Cancel
                  </button>
                </div>
                
                {/* Show last generated content below */}
                <div className={styles.aiLastContent}>
                  <div className={styles.aiLastContentHeader}>Last Generated Content:</div>
                  <div className={styles.aiMarkdownContent}>
                    <Markdown options={{
                      overrides: {
                        p: {
                          component: 'div',
                          props: {
                            style: { marginBottom: '1em' }
                          }
                        }
                      },
                      forceBlock: true
                    }}>
                      {(content.text || '').replace(/\n/g, '  \n')}
                    </Markdown>
                  </div>
                </div>
              </div>
            );
          } else {
            // Show clean markdown content
            return (
              <div className={styles.aiMarkdownContent}>
                <Markdown options={{
                  overrides: {
                    p: {
                      component: 'div',
                      props: {
                        style: { marginBottom: '1em' }
                      }
                    }
                  },
                  forceBlock: true
                }}>
                  {(content.text || '').replace(/\n/g, '  \n')}
                </Markdown>
              </div>
            );
          }
        }
      } else {
        // View mode - show content normally
        return (
          <div 
            className={styles.aiViewContent}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(content.text || '', 'view')) }}
          />
        );
      }
    }
    
    // Fallback for view mode with no content
    return (
      <div className={styles.aiPlaceholder}>
        <Brain size={16} />
        <span>AI content will appear here after generation</span>
      </div>
    );
  };

  // Main render function
  const renderBlockContent = () => {
    switch (type) {
      case 'text':
        return renderTextBlock();
      case 'ai':
        return renderAIBlock();
      case 'title':
        return renderTitleBlock();
      case 'heading':
        return renderHeadingBlock();
      case 'kpi':
        return renderKpiBlock();
      case 'chart':
        return renderChartBlock();
      case 'table':
        return renderTableBlock();
      case 'quote':
        return renderQuoteBlock();
      case 'divider':
        return renderDividerBlock();
      case 'video':
        return renderVideoBlock();
      case 'financial-summary':
        return renderFinancialSummaryBlock();
      case 'reportBlock':
        return renderReportBlock();
      // New advanced block types
      case 'card-container':
        return renderCardContainer();
      case 'hero-section':
        return renderHeroSection();
      case 'image':
        return renderImageBlock();
      default:
        return <div>Unknown block type: {type}</div>;
    }
  };

  // Card Container Block
  const renderCardContainer = () => {
    const { cards = [], columns = 3, cardStyle = 'default' } = content;
    
    // Initialize empty cards if none exist
    const ensuredCards = cards.length === 0 ? [
      { 
        id: `card-${Date.now()}-0`, 
        title: '', 
        blocks: [
          {
            id: `block-${Date.now()}-0`,
            type: 'text',
            content: { text: 'Enter card content...' }
          }
        ] 
      }
    ] : cards;
    
    if (isEditMode) {
      return (
        <div className={styles.cardContainer}>
          <div className={styles.cardContainerHeader}>
            <select
              value={columns}
              onChange={(e) => updateBlockContent(block.id, { columns: parseInt(e.target.value) })}
              className={styles.cardLayoutSelect}
            >
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
              <option value={4}>4 Columns</option>
            </select>
            
            <select
              value={cardStyle}
              onChange={(e) => updateBlockContent(block.id, { cardStyle: e.target.value })}
              className={styles.cardStyleSelect}
            >
              <option value="default">Default</option>
              <option value="bordered">Bordered</option>
              <option value="elevated">Elevated</option>
            </select>
          </div>
          
          <div 
            className={styles.cardGrid} 
            style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
          >
            {ensuredCards.map((card, cardIndex) => (
              <div key={card.id || cardIndex} className={`${styles.cardItem} ${styles[cardStyle]}`}>
                <div className={styles.cardHeader}>
                  <input
                    type="text"
                    value={applyVariableSubstitutionPlainText(card.title || '')}
                    onChange={(e) => {
                      const newCards = [...ensuredCards];
                      newCards[cardIndex] = { ...card, title: e.target.value };
                      updateBlockContent(block.id, { cards: newCards });
                    }}
                    placeholder="Card title"
                    className={styles.cardTitleInput}
                  />
                  <button
                    onClick={() => {
                      const newCards = ensuredCards.filter((_, i) => i !== cardIndex);
                      updateBlockContent(block.id, { cards: newCards });
                    }}
                    className={styles.removeCardButton}
                    title="Remove card"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                
                <div className={styles.cardContent}>
                  {(card.blocks || []).map((nestedBlock, blockIndex) => (
                    <ReportBlock
                      key={nestedBlock.id}
                      block={nestedBlock}
                      mode={mode}
                      onUpdate={(updatedBlock) => {
                        const newCards = [...ensuredCards];
                        const newBlocks = [...(card.blocks || [])];
                        newBlocks[blockIndex] = updatedBlock;
                        newCards[cardIndex] = { ...card, blocks: newBlocks };
                        updateBlockContent(block.id, { cards: newCards });
                      }}
                      onDelete={() => {
                        const newCards = [...ensuredCards];
                        const newBlocks = (card.blocks || []).filter((_, i) => i !== blockIndex);
                        newCards[cardIndex] = { ...card, blocks: newBlocks };
                        updateBlockContent(block.id, { cards: newCards });
                      }}
                      updateBlockContent={(blockId, newContent) => {
                        const newCards = [...ensuredCards];
                        const newBlocks = [...(card.blocks || [])];
                        const targetBlockIndex = newBlocks.findIndex(b => b.id === blockId);
                        if (targetBlockIndex !== -1) {
                          newBlocks[targetBlockIndex] = { 
                            ...newBlocks[targetBlockIndex], 
                            content: { ...newBlocks[targetBlockIndex].content, ...newContent }
                          };
                          newCards[cardIndex] = { ...card, blocks: newBlocks };
                          updateBlockContent(block.id, { cards: newCards });
                        }
                      }}
                      index={blockIndex}
                      draggable={false}
                    />
                  ))}
                  
                  <button
                    onClick={() => {
                      const newBlock = {
                        id: `block-${Date.now()}-${Math.random()}`,
                        type: 'text',
                        content: { text: '' }
                      };
                      const newCards = [...ensuredCards];
                      const newBlocks = [...(card.blocks || []), newBlock];
                      newCards[cardIndex] = { ...card, blocks: newBlocks };
                      updateBlockContent(block.id, { cards: newCards });
                    }}
                    className={styles.addBlockToCard}
                  >
                    <Plus size={14} />
                    Add Block
                  </button>
                </div>
              </div>
            ))}
            
            <button
              onClick={() => {
                const newCard = { 
                  id: `card-${Date.now()}-${Math.random()}`,
                  title: '',
                  blocks: [
                    {
                      id: `block-${Date.now()}-${Math.random()}`,
                      type: 'text',
                      content: { text: 'Enter card content...' }
                    }
                  ]
                };
                const newCards = [...ensuredCards, newCard];
                updateBlockContent(block.id, { cards: newCards });
              }}
              className={styles.addCardButton}
            >
              <Plus size={16} />
              Add Card
            </button>
          </div>
        </div>
      );
    }
    
    // View mode
    return (
      <div 
        className={styles.viewCardContainer} 
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {ensuredCards.map((card, cardIndex) => (
          <div key={card.id || cardIndex} className={`${styles.viewCard} ${styles[cardStyle]}`}>
            {card.title && <h3 className={styles.viewCardTitle}>{card.title}</h3>}
            <div className={styles.viewCardContent}>
              {(card.blocks || []).map((nestedBlock, blockIndex) => (
                <ReportBlock
                  key={nestedBlock.id}
                  block={nestedBlock}
                  mode="view"
                  index={blockIndex}
                  draggable={false}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Hero Section Block
  const renderHeroSection = () => {
    const { 
      title = '', 
      subtitle = '', 
      backgroundImage = '', 
      backgroundColor = '#f8fafc',
      alignment = 'center',
      textColor = '#1f2937'
    } = content;
    
    if (isEditMode) {
      return (
        <div className={styles.heroSection}>
          <div 
            className={`${styles.heroContent} ${styles[alignment]}`}
            style={{ 
              backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
              backgroundColor: !backgroundImage ? backgroundColor : 'transparent',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: textColor
            }}
          >
            <div className={styles.heroHeader}>
              <input
                type="text"
                value={applyVariableSubstitutionPlainText(title || '')}
                onChange={(e) => updateBlockContent(block.id, { title: e.target.value })}
                placeholder="Hero title"
                className={styles.heroTitleInput}
                style={{ color: textColor }}
              />
              <input
                type="text"
                value={applyVariableSubstitutionPlainText(subtitle || '')}
                onChange={(e) => updateBlockContent(block.id, { subtitle: e.target.value })}
                placeholder="Hero subtitle"
                className={styles.heroSubtitleInput}
                style={{ color: textColor }}
              />
            </div>
          </div>

          {/* Hero Settings Modal */}
          {showHeroSettings && (
            <div className={styles.settingsModal}>
              <div className={styles.settingsModalContent}>
                <div className={styles.settingsModalHeader}>
                  <h3>Hero Settings</h3>
                  <button 
                    onClick={() => setShowHeroSettings(false)}
                    className={styles.settingsModalClose}
                  >
                    ×
                  </button>
                </div>
                
                <div className={styles.settingsModalBody}>
                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Text Alignment</label>
                    <select
                      value={alignment}
                      onChange={(e) => updateBlockContent(block.id, { alignment: e.target.value })}
                      className={styles.settingsSelect}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>

                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Text Color</label>
                    <div className={styles.colorInputWrapper}>
                      <input
                        type="color"
                        value={textColor}
                        onChange={(e) => updateBlockContent(block.id, { textColor: e.target.value })}
                        className={styles.colorInput}
                      />
                      <input
                        type="text"
                        value={textColor}
                        onChange={(e) => updateBlockContent(block.id, { textColor: e.target.value })}
                        className={styles.colorTextInput}
                        placeholder="#1f2937"
                      />
                    </div>
                  </div>

                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Background Color</label>
                    <div className={styles.colorInputWrapper}>
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => updateBlockContent(block.id, { backgroundColor: e.target.value })}
                        className={styles.colorInput}
                      />
                      <input
                        type="text"
                        value={backgroundColor}
                        onChange={(e) => updateBlockContent(block.id, { backgroundColor: e.target.value })}
                        className={styles.colorTextInput}
                        placeholder="#f8fafc"
                      />
                    </div>
                  </div>
                  
                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Background Image URL</label>
                    <input
                      type="text"
                      value={backgroundImage}
                      onChange={(e) => updateBlockContent(block.id, { backgroundImage: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className={styles.settingsInput}
                    />
                    {backgroundImage && (
                      <button
                        onClick={() => updateBlockContent(block.id, { backgroundImage: '' })}
                        className={styles.clearImageBtn}
                      >
                        Clear Image
                      </button>
                    )}
                  </div>

                  <div className={styles.settingsGroup}>
                    <label className={styles.settingsLabel}>Upload Background Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Validate file type
                          const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
                          if (!validImageTypes.includes(file.type)) {
                            alert('Invalid file type. Please upload a valid image (JPEG, PNG, GIF, WebP, or SVG).');
                            e.target.value = '';
                            return;
                          }

                          // Validate file size (max 5MB)
                          const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
                          if (file.size > maxSizeInBytes) {
                            alert('File size exceeds 5MB. Please upload a smaller image.');
                            e.target.value = '';
                            return;
                          }

                          const reader = new FileReader();
                          reader.onload = (e) => {
                            updateBlockContent(block.id, { backgroundImage: e.target.result });
                          };
                          reader.onerror = () => {
                            alert('Error reading file. Please try again.');
                            e.target.value = '';
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className={styles.fileInput}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    
    // View mode
    return (
      <div 
        className={`${styles.viewHeroSection} ${styles[alignment]}`}
        style={{ 
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
          backgroundColor: !backgroundImage ? backgroundColor : 'transparent',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: textColor
        }}
      >
        <div className={styles.viewHeroHeader}>
          {title && <h1 className={styles.viewHeroTitle} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(title, 'view')) }} />}
          {subtitle && <p className={styles.viewHeroSubtitle} dangerouslySetInnerHTML={{ __html: sanitizeRichText(applyVariableSubstitution(subtitle, 'view')) }} />}
        </div>
      </div>
    );
  };





  // Render drag handle
  const renderDragHandle = () => {
    if (!isEditMode || isPlaceholder) return null;

    return (
      <div 
        className={styles.blockDragHandle}
        draggable={draggable}
        onDragStart={(e) => {
          if (onDragStart) {
            onDragStart(e, block, index);
          }
        }}
      >
        <GripVertical className={styles.blockDragIcon} />
      </div>
    );
  };

  // Render edit mode actions
  const renderEditActions = () => {
    if (!isEditMode) return null;
    
    const isLoading = aiLoadingBlocks?.has(block.id);
    const isStreaming = aiStreamingBlocks?.has(block.id);

    return (
      <div className={styles.blockActionsWrapper}>
        {/* Edit button for AI-generated text blocks */}
        {type === 'text' && aiGeneratedBlocks?.has(block.id) && editingMarkdown?.[block.id] === undefined && (
          <button
            onClick={() => {
              setEditingMarkdown(prev => ({ ...prev, [block.id]: true }));
            }}
            className={styles.blockActionButton}
            title="Edit"
          >
            <Edit3 size={14} />
          </button>
        )}

        {/* Settings button for AI-generated text blocks */}
        {type === 'text' && aiGeneratedBlocks?.has(block.id) && editingMarkdown?.[block.id] === undefined && (
          <button
            onClick={() => handleAISettingsClick?.(block.id)}
            className={styles.blockActionButton}
            title="Regenerate"
          >
            <Sparkles size={14} />
          </button>
        )}

        {/* Settings button for AI blocks */}
        {type === 'ai' && !isLoading && !isStreaming && !editingMarkdown?.[block.id]?.isEditing && editingMarkdown?.[block.id] !== true && (
          <button
            onClick={() => {
              // Store original prompt for cancel functionality
              if (setEditingMarkdown) {
                setEditingMarkdown(prev => ({ 
                  ...prev, 
                  [block.id]: { 
                    isEditing: true, 
                    originalPrompt: content.prompt || '' 
                  } 
                }));
              }
            }}
            className={styles.blockActionButton}
            title="Edit AI Prompt"
          >
            <Settings size={14} />
          </button>
        )}

        {/* Enhance with AI button for AI blocks */}
        {type === 'ai' && !isLoading && !isStreaming && !editingMarkdown?.[block.id]?.isEditing && editingMarkdown?.[block.id] !== true && (
          <button
            onClick={() => handleEnhanceWithAI?.(block.id)}
            className={styles.blockActionButton}
            title="Enhance with AI"
          >
            <Wand2 size={14} />
          </button>
        )}

        {/* Settings button for heading blocks */}
        {type === 'heading' && (
          <button
            onClick={() => handleAISettingsClick?.(block.id)}
            className={styles.blockActionButton}
            title="Heading Settings"
          >
            <Settings size={14} />
          </button>
        )}

        {/* Settings button for video blocks */}
        {type === 'video' && (
          <button
            onClick={() => handleAISettingsClick?.(block.id)}
            className={styles.blockActionButton}
            title="Video Settings"
          >
            <Settings size={14} />
          </button>
        )}

        {/* Settings button for hero blocks */}
        {type === 'hero-section' && (
          <button
            onClick={() => setShowHeroSettings(true)}
            className={styles.blockActionButton}
            title="Hero Settings"
          >
            <Settings size={14} />
          </button>
        )}

        {/* Settings button for image blocks */}
        {type === 'image' && (
          <button
            onClick={() => setShowImageSettings(true)}
            className={styles.blockActionButton}
            title="Image Settings"
          >
            <Settings size={14} />
          </button>
        )}

        {/* Settings button for chart blocks */}
        {type === 'chart' && (
          <button
            onClick={() => handleAISettingsClick?.(block.id)}
            className={styles.blockActionButton}
            title="Chart Settings"
          >
            <Settings size={14} />
          </button>
        )}

        {/* Settings button for report blocks */}
        {type === 'reportBlock' && (
          <button
            onClick={() => setShowReportSettings(true)}
            className={styles.blockActionButton}
            title="Report Settings"
          >
            <Settings size={14} />
          </button>
        )}

        {/* Enhance with AI button for text blocks */}
        {type === 'text' && (
          <button
            onClick={() => handleEnhanceWithAI?.(block.id)}
            className={styles.blockActionButton}
            title="Enhance with AI"
          >
            <Wand2 size={14} />
          </button>
        )}

        <button
          onClick={() => onDuplicate?.(block.id)}
          className={styles.blockActionButton}
          title="Duplicate"
        >
          <Copy size={14} />
        </button>

        <button
          onClick={() => onDelete?.(block.id)}
          className={styles.blockActionButton}
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    );
  };


  return (
    <>
      <div 
        className={`
          ${styles.block} 
          ${styles[mode]} 
          ${styles[type]}
          ${isEditing ? styles.editing : ''}
          ${isPlaceholder ? styles.placeholder : ''}
        `}
      >
        {renderDragHandle()}
        {!isPlaceholder && renderEditActions()}
        <div className={styles.blockContent}>
          {renderBlockContent()}
        </div>
      </div>
    </>
  );
};

export default ReportBlock;